import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, errorMessage } from "../../api/client";
import { useFields, useImport, useImportIssues } from "../../api/hooks";
import type { ColumnMapping, FieldDef, ImportReport } from "../../api/types";
import { Chip, DATA_TYPES, Empty, IMPORT_STATUS, Icon, Kpi, PageHead, Segmented, Spinner, useToast, type Tone } from "../../components/ui";
import { bytes, duration, fmt } from "../../lib/format";

const METHOD: Record<string, [string, Tone]> = {
  alias: ["Déjà validé", "green"], synonym: ["Reconnu", "green"], existing: ["Champ existant", "green"], fuzzy: ["Nom proche", "orange"],
  content: ["Deviné au contenu", "orange"], new: ["Nouvelle donnée", "blue"], empty: ["Colonne vide", "gray"], manual: ["Choix manuel", "purple"],
};
const CREATE_TYPES = ["text", "integer", "decimal", "date", "boolean", "email", "phone", "url"];

type Draft = Pick<ColumnMapping, "action" | "entity" | "key" | "label" | "data_type">;

function MappingTable({ job, fields }: { job: ImportReport; fields: FieldDef[] }) {
  const cols = job.mapping.columns ?? [];
  const [draft, setDraft] = useState<Record<number, Draft>>({});
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  const targets = useMemo(() => fields.filter((f) => !f.derived && f.status !== "archived"), [fields]);
  const cur = (c: ColumnMapping): Draft => draft[c.index] ?? { action: c.action, entity: c.entity, key: c.key, label: c.label, data_type: c.data_type };
  const set = (c: ColumnMapping, patch: Partial<Draft>) => setDraft({ ...draft, [c.index]: { ...cur(c), ...patch } });
  const review = cols.filter((c) => c.needs_review && !draft[c.index]).length;

  async function start() {
    setBusy(true);
    try {
      const changed = Object.entries(draft).map(([i, d]) => ({ index: Number(i), ...d }));
      if (changed.length) await api(`/imports/${job.id}/mapping`, { method: "PUT", json: { columns: changed } });
      await api(`/imports/${job.id}/start`, { method: "POST" });
      toast("Import lancé : le CRM reste utilisable pendant le traitement.");
      qc.invalidateQueries({ queryKey: ["import", job.id] });
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tile flush">
      <div className="thead" style={{ padding: "16px 18px 6px" }}>
        <div className="stack" style={{ gap: 2 }}>
          <h2>Correspondance des colonnes</h2>
          <span className="muted">Aucune colonne n'est ignorée d'office. Une colonne inconnue devient une donnée de la Master Data, filtrable et exportable.</span>
        </div>
        <div className="row">
          {review > 0 && <Chip tone="orange" dot>{review} à vérifier</Chip>}
          <button className="btn primary" type="button" onClick={start} disabled={busy}><Icon name="check" />{busy ? "Lancement…" : "Valider et lancer l'import"}</button>
        </div>
      </div>
      <div className="scroll">
        <table className="table">
          <thead><tr><th>Colonne du fichier</th><th>Détection</th><th>Action</th><th>Destination</th></tr></thead>
          <tbody>
            {cols.map((c) => {
              const d = cur(c);
              const [m, tone] = METHOD[draft[c.index] ? "manual" : c.method] ?? [c.method, "gray"];
              return (
                <tr key={c.index} className={`map-row${c.needs_review && !draft[c.index] ? " review" : ""}`}>
                  <td style={{ maxWidth: 320 }}>
                    <b>{c.header || <i className="muted">sans en-tête</i>}</b>
                    <div className="sample">{c.sample.length ? c.sample.map((s, i) => <span key={i} title={s}>{s}</span>) : <span>vide</span>}</div>
                  </td>
                  <td>
                    <Chip tone={tone} dot>{m}</Chip>
                    {c.confidence > 0 && c.confidence < 100 && !draft[c.index] && <div className="faint" style={{ fontSize: 12.5, marginTop: 4 }}>confiance {c.confidence} %</div>}
                    {c.related_field_key && d.action === "create" && <div className="faint" style={{ fontSize: 12.5, marginTop: 4 }}>proche de « {fields.find((f) => f.key === c.related_field_key)?.label ?? c.related_field_key} »</div>}
                  </td>
                  <td>
                    <select className="select sm" aria-label={`Action pour ${c.header}`} value={d.action}
                      onChange={(e) => set(c, { action: e.target.value as Draft["action"], ...(e.target.value === "create" ? { entity: d.entity ?? "company", key: d.key ?? c.header, label: d.label ?? c.header, data_type: CREATE_TYPES.includes(d.data_type ?? "") ? d.data_type : "text" } : {}) })}>
                      <option value="map">Associer à un champ</option>
                      <option value="create">Créer une nouvelle donnée</option>
                      <option value="ignore">Ignorer (choix explicite)</option>
                    </select>
                  </td>
                  <td>
                    {d.action === "map" && (
                      <select className="select sm" aria-label={`Champ cible pour ${c.header}`} value={`${d.entity}.${d.key}`}
                        onChange={(e) => { const [entity, key] = e.target.value.split("."); set(c, { entity: entity as Draft["entity"], key }); }}>
                        {!targets.some((f) => f.entity === d.entity && f.key === d.key) && <option value={`${d.entity}.${d.key}`}>—</option>}
                        <optgroup label="Entreprise">{targets.filter((f) => f.entity === "company").map((f) => <option key={f.id} value={`company.${f.key}`}>{f.label}</option>)}</optgroup>
                        <optgroup label="Contact">{targets.filter((f) => f.entity === "contact").map((f) => <option key={f.id} value={`contact.${f.key}`}>{f.label}</option>)}</optgroup>
                      </select>
                    )}
                    {d.action === "create" && (
                      <div className="row" style={{ flexWrap: "nowrap" }}>
                        <input className="input sm" aria-label="Nom de la nouvelle donnée" value={d.label ?? ""} onChange={(e) => set(c, { label: e.target.value, key: e.target.value })} style={{ width: 170 }} />
                        <select className="select sm" aria-label="Type" value={d.data_type ?? "text"} onChange={(e) => set(c, { data_type: e.target.value })}>
                          {CREATE_TYPES.map((t) => <option key={t} value={t}>{DATA_TYPES[t]}</option>)}
                        </select>
                        <select className="select sm" aria-label="Rattachée à" value={d.entity ?? "company"} onChange={(e) => set(c, { entity: e.target.value as Draft["entity"] })}>
                          <option value="company">Entreprise</option><option value="contact">Contact</option>
                        </select>
                      </div>
                    )}
                    {d.action === "ignore" && <span className="muted">Non importée (le fichier source reste archivé)</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Report({ job }: { job: ImportReport }) {
  const r = job.report as Record<string, number>;
  const newFields = (job.report.nouveaux_champs as string[] | null) ?? [];
  const [sev, setSev] = useState<"all" | "error" | "warning" | "info">("all");
  const issues = useImportIssues(job.id, sev === "all" ? null : sev, true);
  return (
    <>
      <div className="kpis">
        <Kpi label="Lignes reçues" value={fmt(r.lignes_recues)} sub={`${fmt(r.lignes_vides_ignorees)} vides ignorées`} />
        <Kpi label="Lignes valides" value={fmt(r.lignes_valides)} tone="good" sub={`${fmt(r.lignes_invalides)} invalides`} />
        <Kpi label="Nouvelles entreprises" value={<span className="grad">{fmt(r.nouvelles_entreprises)}</span>} sub={`${fmt(r.entreprises_rapprochees)} rapprochées de fiches existantes`} focus />
        <Kpi label="Nouveaux contacts" value={fmt(r.nouveaux_contacts)} sub={`${fmt(r.contacts_rapproches)} rapprochés`} />
        <Kpi label="Enrichissements" value={fmt(r.enrichissements)} tone="info" sub={`${fmt(r.coordonnees_ajoutees)} coordonnées ajoutées`} />
        <Kpi label="Doublons à revoir" value={fmt(r.doublons_candidats)} tone={r.doublons_candidats ? "warn" : undefined} sub={`${fmt(r.fusions_auto_notifiees)} rapprochements auto notifiés`} />
        <Kpi label="Conflits conservés" value={fmt(r.conflits_conserves)} tone={r.conflits_conserves ? "warn" : undefined} sub="valeurs différentes tracées, non écrasées" />
        <Kpi label="Anomalies" value={fmt((r.anomalies ?? 0) + (r.valeurs_invalides ?? 0))} tone={(r.valeurs_invalides ?? 0) > 0 ? "bad" : undefined} sub={`${fmt(r.corrections)} corrections prudentes`} />
        <Kpi label="Durée" value={duration(r.duree_ms)} sub={`${bytes(r.volume_octets)} · ${fmt(r.lignes_par_seconde)} lignes/s`} />
      </div>
      {newFields.length > 0 && (
        <div className="banner info"><Icon name="plus" /><span><b>{newFields.length} nouvelle{newFields.length > 1 ? "s" : ""} donnée{newFields.length > 1 ? "s" : ""}</b> ajoutée{newFields.length > 1 ? "s" : ""} à la Master Data : {newFields.join(", ")}. Déjà disponibles dans les filtres et les exports. <Link to="/master-data/fields">Voir le registre</Link></span></div>
      )}
      <div className="tile flush">
        <div className="thead" style={{ padding: "14px 18px" }}>
          <h2>Anomalies ligne par ligne</h2>
          <Segmented label="Gravité" value={sev} onChange={setSev} options={[["all", "Toutes"], ["error", "Erreurs"], ["warning", "Avertissements"], ["info", "Infos"]]} />
        </div>
        <div className="scroll" style={{ maxHeight: 460 }}>
          <table className="table">
            <thead><tr><th className="r">Ligne</th><th>Gravité</th><th>Champ</th><th>Explication</th><th>Valeur reçue</th></tr></thead>
            <tbody>
              {issues.data?.items.map((i) => (
                <tr key={i.id}>
                  <td className="r num">{i.row ?? "–"}</td>
                  <td><Chip tone={i.severity === "error" ? "red" : i.severity === "warning" ? "orange" : "blue"} dot>{i.severity === "error" ? "Erreur" : i.severity === "warning" ? "Attention" : "Info"}</Chip></td>
                  <td>{i.field ?? "–"}</td>
                  <td>{i.message}</td>
                  <td className="mono" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={i.raw ?? ""}>{i.raw ?? "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {issues.data?.items.length === 0 && <Empty title="Aucune anomalie" />}
        </div>
      </div>
    </>
  );
}

export function ImportDetailPage() {
  const id = Number(useParams().id);
  const job = useImport(id);
  const fields = useFields(true);
  const j = job.data;
  const toast = useToast();
  const qc = useQueryClient();
  // Un import terminé change la base : nouveaux champs (filtres, exports), fiches, doublons.
  useEffect(() => {
    if (j?.status !== "completed") return;
    for (const k of ["fields", "companies", "companies-count", "company", "duplicates", "imports"]) qc.invalidateQueries({ queryKey: [k] });
  }, [j?.status, qc]);
  const restart = async () => {
    try {
      await api(`/imports/${id}/start`, { method: "POST" });
      job.refetch();
    } catch (e) {
      toast(errorMessage(e), true);
    }
  };
  const st = j ? IMPORT_STATUS[j.status] ?? { label: j.status, tone: "gray" as const } : null;
  const p = j?.detected.profile;
  return (
    <main className="page">
      <PageHead eyebrow={<Link to="/master-data/imports">← Imports</Link>} title={`Import #${id}`}
        right={st && <Chip tone={st.tone} dot solid={j?.status === "failed"}>{st.label}</Chip>} />
      {job.isLoading && <Spinner />}
      {job.error && <div className="banner bad">{errorMessage(job.error)}</div>}
      {j && p && (
        <div className="row muted" style={{ fontSize: 14 }}>
          <Chip tone="gray">{p.format.toUpperCase()}</Chip>
          {p.encoding && <span>encodage {p.encoding}</span>}
          {p.delimiter && <span>· séparateur « {p.delimiter === "\t" ? "tab" : p.delimiter} »</span>}
          <span>· en-tête ligne {p.header_row + 1}</span>
          {j.detected.summary && <span>· {j.detected.summary.mapped} colonnes reconnues, {j.detected.summary.new_fields} nouvelles</span>}
        </div>
      )}
      {j && ["analyzing", "queued", "running", "uploaded"].includes(j.status) && (
        <div className="tile">
          <div className="row between"><b>{j.status === "running" ? `Traitement en cours · ${fmt(j.progress_rows)} lignes validées` : j.status === "queued" ? "En file d'attente…" : "Analyse du fichier…"}</b><Spinner /></div>
          <div className="progress indet"><i /></div>
          <span className="muted">Vous pouvez quitter cette page : l'import continue en arrière-plan et reprend automatiquement en cas d'incident.</span>
        </div>
      )}
      {j?.status === "failed" && (
        <div className="banner bad" role="alert"><Icon name="alert" />
          <span><b>L'import s'est arrêté.</b> {j.error} Les lots déjà validés sont conservés ; relancer reprend là où il s'est arrêté.</span>
          <button className="btn sm" type="button" style={{ marginLeft: "auto" }} onClick={restart}>Relancer</button>
        </div>
      )}
      {j?.status === "awaiting_mapping" && fields.data && <MappingTable job={j} fields={fields.data} />}
      {j?.status === "completed" && <Report job={j} />}
      {j && !["completed", "awaiting_mapping"].includes(j.status) && j.progress_rows > 0 && <Report job={j} />}
    </main>
  );
}
